import { useEffect, useCallback, useRef, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import { comparisonActions } from '../../store/slices/comparison.slice';

const usePerformanceComparison = ({ initialObjects, benchmark, relativeMetrics }) => {
  const dispatch = useDispatch();
  const [showHistory, setShowHistory] = useState(false);
  const [selectedExtraItems, setSelectedExtraItems] = useState([]);
  const { items, commonStartDate, loading, itemColors, history, showSearch } = useSelector(state => state.comparison);
  const [activeTab, setActiveTab] = useState('1');
  const previousInitialObjects = useRef(null);

  const fetchTimeSeriesData = useCallback(({ symbols, benchmark = undefined, labels }) => {
    if (!loading) {
      dispatch(comparisonActions.setShowSearch(false));
      const fullSymbolPayload = [...symbols];
      if (benchmark) {
        fullSymbolPayload.push(...Array.isArray(benchmark) ? benchmark : [benchmark]);
      }
      dispatch(comparisonActions.fetchTimeSeriesData({
        symbols: fullSymbolPayload,
        startDate: commonStartDate,
        labels: labels
      }));
    }
  }, [dispatch, loading, commonStartDate]);

  useEffect(() => {
    if (!_.isEqual(previousInitialObjects.current, initialObjects)) {
      previousInitialObjects.current = initialObjects;
      if (initialObjects) {
        const symbols = initialObjects.map(item => item.id);
        const labels = initialObjects.map(item => ({ id: item.id, label: item.label }));
        fetchTimeSeriesData({ symbols, labels });
      }
    }
  }, [initialObjects, fetchTimeSeriesData]);

  const handleSubmit = useCallback((itemsToSubmit) => {
    const newItems = itemsToSubmit.hasOwnProperty('items') ? itemsToSubmit.items : itemsToSubmit;
    fetchTimeSeriesData({ symbols: newItems.map(item => item.id) });
  }, [fetchTimeSeriesData]);

  useEffect(() => {
    if (activeTab === '8' && items.length > 0) {
      relativeMetrics.fetchNewData(items.map(x => x.id), null, commonStartDate);
    }
  }, [activeTab, items, relativeMetrics.targetSymbol, relativeMetrics.metric, relativeMetrics.window]);

  const handleTickersSubmit = useCallback((selectedTickers) => {
    const symbols = selectedTickers.map(x => x.symbol);
    const labels = selectedTickers.map(x => ({ id: x.symbol, label: x.name }));
    fetchTimeSeriesData({ symbols, labels });
  }, [fetchTimeSeriesData]);

  const handlePortfolioSubmit = useCallback((selectedPortfolios) => {
    const ids = selectedPortfolios.map(x => x.id);
    const labels = selectedPortfolios.map(x => ({ id: x.id, label: x.name }));
    fetchTimeSeriesData({ symbols: ids, labels });
  }, [fetchTimeSeriesData]);

  const handleRemoveItem = useCallback((id) => {
    dispatch(comparisonActions.removeItem({ id }));
  }, [dispatch]);

  const toggleExtraItems = useCallback((ids) => {
    setSelectedExtraItems([...selectedExtraItems, ...ids]);
    fetchTimeSeriesData({ symbols: ids });
  }, [selectedExtraItems, fetchTimeSeriesData]);

  const setShowSearch = useCallback((value) => {
    dispatch(comparisonActions.setShowSearch(value));
  }, [dispatch]);

  const reset = useCallback(() => {
    dispatch(comparisonActions.resetComparison());
    setShowSearch(true);
    setSelectedExtraItems([]);
  }, [dispatch, setShowSearch]);

  const toggleHistory = useCallback(() => {
    setShowHistory(prev => !prev);
  }, []);

  const mapColorsToLabels = useMemo(() => {
    const newMapping = {};
    items.forEach(item => {
      newMapping[item.label] = itemColors?.[item.id] || '#000000';
      if (item.id !== item.label) {
        newMapping[item.id] = itemColors?.[item.id] || '#000000';
      }
    });
    return newMapping;
  }, [items, itemColors]);

  return {
    showSearch,
    setShowSearch,
    showHistory,
    toggleHistory,
    selectedExtraItems,
    activeTab,
    setActiveTab,
    handleSubmit,
    handleTickersSubmit,
    handlePortfolioSubmit,
    handleRemoveItem,
    toggleExtraItems,
    reset,
    mapColorsToLabels,
    history,
  };
};

export default usePerformanceComparison;